import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ConnectButton } from "@rainbow-me/rainbowkit";
import Icon from "./Icon";
import { navItems, moreMenuItems } from "../constants";

const Header = ({ activePage = "" }) => {
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const moreMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const location = useLocation();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target)) {
        setIsMoreOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Close dropdown when route changes
  useEffect(() => {
    setIsMoreOpen(false);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      <nav className="bg-[#050505] border-b border-yellow-500/20 px-4 lg:px-6 py-4 flex justify-between items-center sticky top-0 z-50 backdrop-blur-md bg-[#050505]/80">
        <div className="flex items-center gap-4">
          <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-2">
            <img
              src="/logo.png"
              alt="IO Trader"
              className="h-5 md:h-6 object-contain cursor-pointer"
            />
          
          </Link>
          
          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-gray-400 hover:text-yellow-400 transition-colors p-2"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <Icon name="X" size={24} />
            ) : (
              <Icon name="Menu" size={24} />
            )}
          </button>
        </div>

      <div className="hidden lg:flex items-center space-x-8 text-sm font-bold text-gray-400">
        {navItems.map((item) => {
          const isActive = location.pathname === item.href || 
            (item.href === "/" && location.pathname === "/") ||
            (item.href !== "/" && location.pathname.startsWith(item.href));
          return (
            <Link
              key={item.name}
              to={item.href}
              className={
                isActive
                  ? "text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 to-yellow-500"
                  : "hover:text-yellow-400 transition-colors"
              }
            >
              {item.name}
            </Link>
          );
        })}
        <div 
          className="relative" 
          ref={moreMenuRef}
        >
          <button
            onClick={() => setIsMoreOpen(!isMoreOpen)}
            className={`flex items-center cursor-pointer transition-colors ${
              isMoreOpen 
                ? "text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 to-yellow-500"
                : "text-gray-400 hover:text-yellow-400"
            }`}
          >
            <span>More</span>
            <Icon
              name="ChevronDown"
              size={14}
              className={`ml-1 transition-transform duration-300 ${
                isMoreOpen ? "rotate-180" : ""
              }`}
            />
          </button>
          
          {/* Dropdown Menu */}
          {isMoreOpen && (
            <div className="absolute top-full right-0 mt-2 w-56 bg-[#0a0a0a] border border-yellow-500/30 rounded-lg shadow-xl z-50 overflow-hidden">
              <div className="py-2">
                {moreMenuItems.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setIsMoreOpen(false)}
                      className={`block px-4 py-2 text-sm font-bold transition-colors ${
                        isActive
                          ? "text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 to-yellow-500 bg-yellow-500/10"
                          : "text-gray-400 hover:text-yellow-400 hover:bg-white/5"
                      }`}
                    >
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right: Connect Wallet with RainbowKit */}
      <div className="flex items-center gap-3" style={{ zIndex: 1000 }}>
        <ConnectButton.Custom>
          {({
            account,
            chain,
            openAccountModal,
            openChainModal,
            openConnectModal,
            authenticationStatus,
            mounted,
          }) => {
            const ready = mounted && authenticationStatus !== "loading";
            const connected =
              ready &&
              account &&
              chain &&
              (!authenticationStatus ||
                authenticationStatus === "authenticated");

            // Debug logging
            if (process.env.NODE_ENV === 'development') {
              console.log('ConnectButton state:', { ready, connected, mounted, authenticationStatus });
            }

            return (
              <div
                {...(!ready && {
                  "aria-hidden": true,
                  style: {
                    opacity: 0,
                    pointerEvents: "none",
                    userSelect: "none",
                  },
                })}
              >
                {(() => {
                  if (!connected) {
                    return (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('Connect button clicked, opening modal...');
                          if (openConnectModal) {
                            openConnectModal();
                          } else {
                            console.error('openConnectModal is not available');
                          }
                        }}
                        type="button"
                        className="flex items-center gap-2 bg-gradient-to-r from-yellow-600 to-yellow-400 text-black font-black text-sm px-3 sm:px-6 py-2 rounded-lg shadow-[0_0_15px_rgba(234,179,8,0.3)] hover:shadow-[0_0_25px_rgba(234,179,8,0.5)] transition-all transform hover:-translate-y-0.5 active:translate-y-0 touch-manipulation cursor-pointer"
                        aria-label="Connect Wallet"
                      >
                        <span className="relative flex h-2.5 w-2.5">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-black opacity-50"></span>
                          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-black"></span>
                        </span>
                        <Icon name="Wallet" size={16} strokeWidth={3} />
                        <span className="hidden sm:inline">Connect Wallet</span>
                      </button>
                    );
                  }

                  if (chain.unsupported) {
                    return (
                      <button
                        onClick={openChainModal}
                        type="button"
                        className="flex items-center gap-2 bg-red-600 text-white font-black text-sm px-3 sm:px-6 py-2 rounded-lg hover:bg-red-700 transition-all"
                      >
                        Wrong network
                      </button>
                    );
                  }

                  return (
                    <div className="flex items-center gap-3">
                      <button
                        onClick={openChainModal}
                        type="button"
                        className="flex items-center gap-1.5 bg-[#1a1a1a] border border-white/10 text-white p-2 rounded-lg hover:bg-white/5 transition-colors touch-manipulation"
                      >
                        {chain.hasIcon && (
                          <div
                            style={{
                              background: chain.iconBackground,
                              width: 20,
                              height: 20,
                              borderRadius: 999,
                              overflow: "hidden",
                              marginRight: 4,
                            }}
                          >
                            {chain.iconUrl && (
                              <img
                                alt={chain.name ?? "Chain icon"}
                                src={chain.iconUrl}
                                style={{ width: 20, height: 20 }}
                              />
                            )}
                          </div>
                        )}
                        <span className="hidden sm:inline text-sm font-bold">
                          {chain.name}
                        </span>
                        <Icon
                          name="ChevronDown"
                          size={14}
                          className="text-gray-400"
                        />
                      </button>

                      <button
                        onClick={openAccountModal}
                        type="button"
                        className="flex items-center gap-2 bg-gradient-to-r from-yellow-600 to-yellow-400 text-black font-black text-sm px-3 sm:px-6 py-2 rounded-lg shadow-[0_0_15px_rgba(234,179,8,0.3)] hover:shadow-[0_0_25px_rgba(234,179,8,0.5)] transition-all transform hover:-translate-y-0.5 active:translate-y-0 touch-manipulation"
                      >
                        <span className="relative flex h-2.5 w-2.5">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-black opacity-50"></span>
                          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-black"></span>
                        </span>
                        <Icon name="Wallet" size={16} strokeWidth={3} />
                        <span className="hidden sm:inline">
                          {account.displayName}
                        </span>
                      </button>
                    </div>
                  );
                })()}
              </div>
            );
          }}
        </ConnectButton.Custom>
      </div>
    </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-300"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <div
            ref={mobileMenuRef}
            className="fixed top-0 left-0 h-full w-80 max-w-[85vw] bg-[#0a0a0a] border-r border-yellow-500/20 shadow-2xl overflow-y-auto z-50 transform transition-transform duration-300 ease-in-out"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Mobile Menu Header */}
            <div className="flex items-center justify-between p-4 border-b border-yellow-500/20">
              <Link to="/" onClick={() => setIsMobileMenuOpen(false)}>
                <img
                  src="/logo.png"
                  alt="IO Trader"
                  className="h-8 object-contain"
                />
              </Link>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-gray-400 hover:text-yellow-400 transition-colors p-2"
                aria-label="Close menu"
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            {/* Mobile Menu Items */}
            <div className="py-4">
              {/* Main Navigation Items */}
              <div className="px-4 space-y-1 mb-4">
                {navItems.map((item) => {
                  const isActive = location.pathname === item.href || 
                    (item.href === "/" && location.pathname === "/") ||
                    (item.href !== "/" && location.pathname.startsWith(item.href));
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`block px-4 py-3 text-sm font-bold transition-colors rounded-lg ${
                        isActive
                          ? "text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 to-yellow-500 bg-yellow-500/10"
                          : "text-gray-400 hover:text-yellow-400 hover:bg-white/5"
                      }`}
                    >
                      {item.name}
                    </Link>
                  );
                })}
              </div>

              {/* Divider */}
              <div className="border-t border-yellow-500/20 my-4"></div>

              {/* More Menu Items */}
              <div className="px-4">
                <div className="text-xs font-bold text-gray-500 uppercase tracking-wider px-4 py-2 mb-2">
                  More
                </div>
                <div className="space-y-1">
                  {moreMenuItems.map((item) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`block px-4 py-3 text-sm font-bold transition-colors rounded-lg ${
                          isActive
                            ? "text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 to-yellow-500 bg-yellow-500/10"
                            : "text-gray-400 hover:text-yellow-400 hover:bg-white/5"
                        }`}
                      >
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
